import type { DictionaryWord, ExampleSentence, WordInsight } from "../types";

const trimText = (value: string, maxLength: number) =>
  value.trim().slice(0, maxLength);

const trimList = (values: string[], maxItems: number, maxLength: number) =>
  values
    .filter((value): value is string => typeof value === "string")
    .map((value) => trimText(value, maxLength))
    .filter(Boolean)
    .slice(0, maxItems);

const trimExamples = (examples: ExampleSentence[]) =>
  examples
    .filter(
      (example): example is ExampleSentence =>
        typeof example?.source === "string" && typeof example?.target === "string",
    )
    .map((example) => ({
      source: trimText(example.source, 500),
      target: trimText(example.target, 500),
    }))
    .slice(0, 4);

type BuildDictionaryWordInput = {
  id: string;
  insight: WordInsight;
  context: string;
  sourceLanguage: string;
  targetLanguage: string;
  now?: string;
};

export function buildDictionaryWord({
  id,
  insight,
  context,
  sourceLanguage,
  targetLanguage,
  now = new Date().toISOString(),
}: BuildDictionaryWordInput): DictionaryWord {
  const word: DictionaryWord = {
    id: trimText(id, 200),
    word: trimText(insight.word, 100),
    detectedLanguage: trimText(insight.detectedLanguage, 100),
    translation: trimText(insight.translation, 300),
    sourceLanguage: trimText(sourceLanguage, 100),
    targetLanguage: trimText(targetLanguage, 100),
    pronunciation: trimText(insight.pronunciation, 200),
    partOfSpeech: trimText(insight.partOfSpeech, 100),
    definition: trimText(insight.definition, 1_200),
    contextMeaning: trimText(insight.contextMeaning, 1_200),
    context: trimText(context, 900),
    synonyms: trimList(insight.synonyms, 6, 100),
    antonyms: trimList(insight.antonyms, 6, 100),
    examples: trimExamples(insight.examples),
    note: trimText(insight.note, 1_000),
    createdAt: now,
    updatedAt: now,
  };

  if (insight.sourceDefinition) {
    word.sourceDefinition = trimText(insight.sourceDefinition, 1_200);
  }
  if (insight.translatedDefinition) {
    word.translatedDefinition = trimText(insight.translatedDefinition, 1_200);
  }
  if (insight.contextTranslation) {
    word.contextTranslation = trimText(insight.contextTranslation, 1_200);
  }
  if (insight.model) {
    word.model = trimText(insight.model, 200);
  }
  if (insight.alternativeDefinitions?.length) {
    word.alternativeDefinitions = trimList(insight.alternativeDefinitions, 8, 1_200);
  }
  if (insight.source) {
    word.source = {
      name: trimText(insight.source.name, 200),
      url: trimText(insight.source.url, 1_000),
      license: trimText(insight.source.license, 200),
    };
  }

  return word;
}

export function toFirestoreDictionaryWord(word: DictionaryWord): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    id: word.id,
    word: word.word,
    detectedLanguage: word.detectedLanguage,
    translation: word.translation,
    sourceLanguage: word.sourceLanguage,
    targetLanguage: word.targetLanguage,
    pronunciation: word.pronunciation,
    partOfSpeech: word.partOfSpeech,
    definition: word.definition,
    contextMeaning: word.contextMeaning,
    context: word.context,
    synonyms: word.synonyms,
    antonyms: word.antonyms,
    examples: word.examples,
    note: word.note,
  };

  if (word.sourceDefinition) payload.sourceDefinition = word.sourceDefinition;
  if (word.translatedDefinition) payload.translatedDefinition = word.translatedDefinition;
  if (word.contextTranslation) payload.contextTranslation = word.contextTranslation;
  if (word.model) payload.model = word.model;
  if (word.alternativeDefinitions?.length) {
    payload.alternativeDefinitions = word.alternativeDefinitions;
  }
  if (word.source) payload.source = word.source;

  return payload;
}
